export interface WalletBalance {
  available: number;
  pending: number;
  reserved: number;
  total: number;
  currency: string;
}

export interface Transaction {
  id: string;
  type: 'payment' | 'deposit' | 'withdrawal' | 'fee' | 'refund';
  amount: number;
  description: string;
  date: string;
  status: 'completed' | 'pending' | 'failed';
  category: string;
  recipient?: string;
  reference?: string;
}

export interface PaymentMethod {
  id: string;
  type: 'bank' | 'card' | 'crypto';
  name: string;
  last4?: string;
  expiryDate?: string;
  isDefault: boolean;
  isVerified: boolean;
}

export interface WalletResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

class WalletService {
  private baseUrl = '/api/wallet';

  async getBalance(): Promise<WalletResponse<WalletBalance>> {
    try {
      // Demo data for now
      return {
        success: true,
        data: {
          available: 2847.5,
          pending: 0,
          reserved: 485.0, // HOA dues amount
          total: 3332.5,
          currency: 'USD',
        },
      };

      // When backend is ready:
      // const response = await fetch(`${this.baseUrl}/balance`);
      // return response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async getTransactions(): Promise<WalletResponse<Transaction[]>> {
    try {
      // Demo data
      return {
        success: true,
        data: [
          {
            id: 't1',
            type: 'payment',
            amount: -485.0,
            description: 'HOA Monthly Dues - July 2025',
            date: '2025-07-15T10:00:00Z',
            status: 'completed',
            category: 'HOA Dues',
            recipient: 'Community Association',
            reference: 'HOA-2025-07',
          },
          {
            id: 't2',
            type: 'deposit',
            amount: 125.0,
            description: 'Dividend Payment - Community Investments',
            date: '2025-07-01T14:30:00Z',
            status: 'completed',
            category: 'Investment',
            reference: 'DIV-2025-Q2',
          },
          {
            id: 't3',
            type: 'payment',
            amount: -85.0,
            description: 'Tennis Court Booking - July',
            date: '2025-07-05T16:45:00Z',
            status: 'completed',
            category: 'Facilities',
            recipient: 'Facilities Management',
            reference: 'FAC-TENNIS-2025-07',
          },
          {
            id: 't4',
            type: 'payment',
            amount: -25.0,
            description: 'Community Event Ticket - BBQ',
            date: '2025-07-28T09:15:00Z',
            status: 'pending',
            category: 'Events',
            recipient: 'Event Committee',
            reference: 'EVT-BBQ-2025',
          },
        ],
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async getPaymentMethods(): Promise<WalletResponse<PaymentMethod[]>> {
    try {
      // Demo data
      return {
        success: true,
        data: [
          {
            id: 'pm1',
            type: 'bank',
            name: 'Chase Checking',
            last4: '4532',
            isDefault: true,
            isVerified: true,
          },
          {
            id: 'pm2',
            type: 'card',
            name: 'Visa Credit Card',
            last4: '8901',
            expiryDate: '12/26',
            isDefault: false,
            isVerified: true,
          },
        ],
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async makePayment(
    amount: number,
    description: string,
    paymentMethodId: string,
  ): Promise<WalletResponse> {
    try {
      // Backend implementation placeholder
      console.log('Processing payment:', { amount, description, paymentMethodId });

      // Simulate processing delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return { success: true };

      // When backend is ready:
      // const response = await fetch(`${this.baseUrl}/payments`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ amount, description, paymentMethodId }),
      // });
      // return response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async addPaymentMethod(paymentMethodData: Omit<PaymentMethod, 'id'>): Promise<WalletResponse> {
    try {
      // Backend implementation placeholder
      console.log('Adding payment method:', paymentMethodData);
      return { success: true };

      // When backend is ready:
      // const response = await fetch(`${this.baseUrl}/payment-methods`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(paymentMethodData),
      // });
      // return response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async transferFunds(
    toUserId: string,
    amount: number,
    description: string,
  ): Promise<WalletResponse> {
    try {
      console.log('Transferring funds:', { toUserId, amount, description });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async requestRefund(transactionId: string, reason: string): Promise<WalletResponse> {
    try {
      console.log('Requesting refund:', { transactionId, reason });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

export const walletService = new WalletService();
